import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { CartItemInput } from "@baze/api-client";
import { track } from "@/lib/meta-pixel";

/**
 * Per-website cart (persisted in localStorage). The server is the source of truth for prices
 * (api.commerce.quote); this store only remembers WHAT the user wants to buy for each site.
 */

export function cartItemKey(item: CartItemInput): string {
  switch (item.kind) {
    case "template":
    case "design":
      return `${item.kind}:${item.id}`;
    case "site_plan":
      return "site_plan"; // one Paket per order
    case "domain_register":
      return `domain_register:${item.domain.toLowerCase()}`;
    case "domain_renew":
      return `domain_renew:${item.domainId}`;
  }
}

export function hasYears(item: CartItemInput): item is Extract<CartItemInput, { years: number }> {
  return item.kind === "site_plan" || item.kind === "domain_register" || item.kind === "domain_renew";
}

interface CartState {
  carts: Record<string, CartItemInput[]>;
  /** Site whose cart sheet is open (null = closed). Not persisted. */
  openFor: string | null;
  add: (siteId: string, item: CartItemInput) => void;
  addMany: (siteId: string, items: CartItemInput[]) => void;
  remove: (siteId: string, key: string) => void;
  setYears: (siteId: string, key: string, years: number) => void;
  clear: (siteId: string) => void;
  open: (siteId: string) => void;
  close: () => void;
}

function upsert(list: CartItemInput[], item: CartItemInput): CartItemInput[] {
  const key = cartItemKey(item);
  const idx = list.findIndex((i) => cartItemKey(i) === key);
  if (idx === -1) return [...list, item];
  const next = [...list];
  next[idx] = item;
  return next;
}

export const useCart = create<CartState>()(
  persist(
    (set) => ({
      carts: {},
      openFor: null,
      add: (siteId, item) => {
        track("AddToCart", { content_ids: [cartItemKey(item)], content_type: "product" });
        set((s) => ({ carts: { ...s.carts, [siteId]: upsert(s.carts[siteId] ?? [], item) } }));
      },
      addMany: (siteId, items) => {
        if (items.length) track("AddToCart", { content_ids: items.map(cartItemKey), content_type: "product" });
        set((s) => ({ carts: { ...s.carts, [siteId]: items.reduce(upsert, s.carts[siteId] ?? []) } }));
      },
      remove: (siteId, key) => set((s) => ({ carts: { ...s.carts, [siteId]: (s.carts[siteId] ?? []).filter((i) => cartItemKey(i) !== key) } })),
      setYears: (siteId, key, years) =>
        set((s) => ({
          carts: {
            ...s.carts,
            [siteId]: (s.carts[siteId] ?? []).map((i) => (cartItemKey(i) === key && hasYears(i) ? { ...i, years: Math.max(1, Math.min(10, Math.round(years))) } : i)),
          },
        })),
      clear: (siteId) =>
        set((s) => {
          const { [siteId]: _removed, ...rest } = s.carts;
          return { carts: rest };
        }),
      open: (siteId) => set({ openFor: siteId }),
      close: () => set({ openFor: null }),
    }),
    {
      name: "baze:carts",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ carts: s.carts }),
    },
  ),
);

const EMPTY: CartItemInput[] = [];
export const useSiteCart = (siteId: string | undefined) => useCart((s) => (siteId ? (s.carts[siteId] ?? EMPTY) : EMPTY));

/** Reconstruct cart items from an order (retry after failed/expired payment). */
export function itemsFromOrder(items: Array<{ kind: CartItemInput["kind"]; ref: string; years?: number }>): CartItemInput[] {
  return items.map((i): CartItemInput => {
    switch (i.kind) {
      case "template":
        return { kind: "template", id: i.ref };
      case "design":
        return { kind: "design", id: i.ref };
      case "site_plan":
        return { kind: "site_plan", id: i.ref, years: i.years ?? 1 };
      case "domain_register":
        return { kind: "domain_register", domain: i.ref, years: i.years ?? 1 };
      case "domain_renew":
        return { kind: "domain_renew", domainId: i.ref, years: i.years ?? 1 };
    }
  });
}
